import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RedesSociales from '../components/RedesSociales';

/*
 * Pagina publica /mercadeo: imagenes, videos y enlaces de redes que el dueno
 * administra en Admin > Mercadeo (/api/mercadeo/).
 * Todas las tarjetas tienen el mismo tamano; al tocar una se abre en una ventana
 * (foto grande o video reproduciendose), con flechas para pasar a la siguiente.
 */

type Elemento = {
  id: number;
  titulo: string;
  descripcion: string;
  tipo: 'imagen' | 'video' | 'enlace';
  archivo_url: string | null;
  enlace_url: string | null;
  categoria_id: number | null;
  prioridad: number;
  visible_hasta: string | null;
};

type Categoria = { id: number; nombre: string; icono: string };

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
const fechaCorta = (iso: string) => { const [, m, d] = iso.split('-'); return `${parseInt(d, 10)} ${MESES[parseInt(m, 10) - 1]}`; };

type Incrustado = { src: string; vertical: boolean; red: string; miniatura: string | null };

// Convierte un enlace de YouTube, TikTok, Instagram o Facebook en un reproductor incrustado.
export function incrustar(url: string): Incrustado | null {
  let u: URL;
  try { u = new URL(url); } catch { return null; }
  const host = u.hostname.replace(/^www\.|^m\./, '');
  const ruta = u.pathname;
  let m: RegExpMatchArray | null;

  const youtube = (id: string, vertical: boolean): Incrustado => ({
    src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`,
    vertical,
    red: 'YouTube',
    miniatura: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
  });
  if (host === 'youtu.be') {
    const id = ruta.split('/')[1];
    if (id) return youtube(id, false);
  }
  if (host.endsWith('youtube.com')) {
    if ((m = ruta.match(/^\/shorts\/([\w-]+)/))) return youtube(m[1], true);
    if ((m = ruta.match(/^\/(?:embed|live)\/([\w-]+)/))) return youtube(m[1], false);
    const v = u.searchParams.get('v');
    if (v) return youtube(v, false);
  }
  if (host.endsWith('tiktok.com') && (m = ruta.match(/\/video\/(\d+)/))) {
    return { src: `https://www.tiktok.com/embed/v2/${m[1]}`, vertical: true, red: 'TikTok', miniatura: null };
  }
  if (host.endsWith('instagram.com') && (m = ruta.match(/^\/(?:[\w.]+\/)?(p|reel|reels|tv)\/([\w-]+)/))) {
    const tipo = m[1] === 'reels' ? 'reel' : m[1];
    return { src: `https://www.instagram.com/${tipo}/${m[2]}/embed`, vertical: true, red: 'Instagram', miniatura: null };
  }
  if ((host.endsWith('facebook.com') && /\/(videos|reel|watch)/.test(ruta + u.search)) || host === 'fb.watch') {
    return {
      src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=true`,
      vertical: /\/reel/.test(ruta),
      red: 'Facebook',
      miniatura: null,
    };
  }
  return null;
}

function dominio(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return 'enlace'; }
}

// Fondo de la tarjeta para enlaces sin miniatura
const FONDO_RED: Record<string, string> = {
  TikTok: 'linear-gradient(135deg, #000 0%, #111 55%, #FE2C55 140%)',
  Instagram: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
  Facebook: 'linear-gradient(135deg, #1877F2 0%, #0b4fb3 100%)',
  YouTube: 'linear-gradient(135deg, #FF0000 0%, #a30000 100%)',
};

const BotonPlay = ({ grande = false }: { grande?: boolean }) => (
  <span className={`absolute inset-0 flex items-center justify-center pointer-events-none`}>
    <span className={`${grande ? 'w-20 h-20' : 'w-14 h-14'} rounded-full bg-black/55 ring-2 ring-white/80 flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:bg-rojo transition-all duration-300`}>
      <svg viewBox="0 0 24 24" className={grande ? 'w-9 h-9 ml-1' : 'w-6 h-6 ml-0.5'} fill="white" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
    </span>
  </span>
);

export default function Mercadeo() {
  const [lista, setLista] = useState<Elemento[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [cargando, setCargando] = useState(true);
  const [params, setParams] = useSearchParams();
  const [abierta, setAbierta] = useState<number | null>(null); // indice en "ordenados"

  useEffect(() => {
    fetch('/api/mercadeo/')
      .then(r => (r.ok ? r.json() : []))
      .then((d: Elemento[]) => { setLista(Array.isArray(d) ? d : []); setCargando(false); })
      .catch(() => { setLista([]); setCargando(false); });
    fetch('/api/mercadeo/categorias')
      .then(r => (r.ok ? r.json() : []))
      .then((d: Categoria[]) => setCategorias(Array.isArray(d) ? d : []))
      .catch(() => setCategorias([]));
  }, []);

  // Solo se muestran las categorias que tienen algo publicado
  const conContenido = categorias.filter(c => lista.some(e => e.categoria_id === c.id));
  const idsCategorias = categorias.map(c => c.id);
  const sinCategoria = lista.filter(e => e.categoria_id === null || idsCategorias.indexOf(e.categoria_id) === -1);
  const pestanas: { valor: string; texto: string; cantidad: number }[] = [
    { valor: '', texto: 'Todo', cantidad: lista.length },
    ...conContenido.map(c => ({ valor: String(c.id), texto: `${c.icono} ${c.nombre}`, cantidad: lista.filter(e => e.categoria_id === c.id).length })),
  ];
  if (conContenido.length > 0 && sinCategoria.length > 0) pestanas.push({ valor: 'otros', texto: 'Otros', cantidad: sinCategoria.length });

  const actual = params.get('categoria') || '';
  const seleccion = pestanas.some(p => p.valor === actual) ? actual : '';
  const elegir = (valor: string) => { setAbierta(null); setParams(valor ? { categoria: valor } : {}, { replace: true }); };

  const visibles = seleccion === '' ? lista
    : seleccion === 'otros' ? sinCategoria
    : lista.filter(e => String(e.categoria_id) === seleccion);
  const destacados = visibles.filter(e => e.prioridad === 1);
  const resto = visibles.filter(e => e.prioridad !== 1);
  const ordenados = destacados.concat(resto);
  const total = ordenados.length;

  useEffect(() => {
    if (abierta === null) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierta(null);
      if (e.key === 'ArrowRight') setAbierta(i => (i === null ? i : (i + 1) % total));
      if (e.key === 'ArrowLeft') setAbierta(i => (i === null ? i : (i - 1 + total) % total));
    };
    document.addEventListener('keydown', tecla);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', tecla); document.body.style.overflow = ''; };
  }, [abierta, total]);

  // ---------- miniatura (todas del mismo tamano) ----------
  const miniatura = (e: Elemento, grande: boolean) => {
    if (e.tipo === 'imagen' && e.archivo_url) {
      return <img src={e.archivo_url} alt={e.titulo || 'TicoBaterías'} loading="lazy"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />;
    }
    if (e.tipo === 'video' && e.archivo_url) {
      return (
        <>
          <video src={`${e.archivo_url}#t=0.1`} preload="metadata" muted playsInline tabIndex={-1}
            className="absolute inset-0 w-full h-full object-cover bg-black pointer-events-none group-hover:scale-105 transition-transform duration-500" />
          <BotonPlay grande={grande} />
        </>
      );
    }
    const inc = e.enlace_url ? incrustar(e.enlace_url) : null;
    if (inc && inc.miniatura) {
      return (
        <>
          <img src={inc.miniatura} alt="" loading="lazy"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <BotonPlay grande={grande} />
        </>
      );
    }
    const redNombre = inc ? inc.red : dominio(e.enlace_url || '');
    return (
      <>
        <span className="absolute inset-0" style={{ background: (inc && FONDO_RED[inc.red]) || 'linear-gradient(135deg, #253789, #1a2860)' }} />
        <span className="absolute bottom-3 left-0 right-0 text-center text-white/90 text-sm font-bold tracking-wide pointer-events-none">{redNombre}</span>
        <BotonPlay grande={grande} />
      </>
    );
  };

  const etiquetaTipo = (e: Elemento) => (e.tipo === 'imagen' ? '' : e.tipo === 'video' ? '🎬 VIDEO' : `🎬 ${incrustar(e.enlace_url || '')?.red.toUpperCase() || 'VIDEO'}`);

  const tarjeta = (e: Elemento, grande: boolean) => {
    const indice = ordenados.findIndex(x => x.id === e.id);
    const tipo = etiquetaTipo(e);
    return (
      <button key={e.id} type="button" onClick={() => setAbierta(indice)}
        aria-label={`Abrir ${e.titulo || (e.tipo === 'imagen' ? 'imagen' : 'video')}`}
        className={`group text-left w-full h-full flex flex-col bg-white rounded-2xl overflow-hidden transition-shadow hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-azul/40 ${grande ? 'shadow-md ring-2 ring-yellow-400' : 'border border-gray-100 shadow-sm'}`}>
        <div className={`relative w-full overflow-hidden bg-gray-200 ${grande ? 'aspect-video' : 'aspect-[4/3]'}`}>
          {miniatura(e, grande)}
          {grande && <span className="absolute top-3 left-3 bg-yellow-400 text-azul-dark text-xs font-black tracking-wider px-3 py-1 rounded-full shadow-md">⭐ DESTACADO</span>}
          {tipo && <span className="absolute top-3 right-3 bg-black/60 text-white text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full">{tipo}</span>}
        </div>
        <div className={`flex-1 ${grande ? 'p-5' : 'p-4 min-h-[5.75rem]'}`}>
          {e.visible_hasta && (
            <div className="inline-block bg-rojo/10 text-rojo text-xs font-bold px-2.5 py-1 rounded-full mb-2">⏳ Válido hasta el {fechaCorta(e.visible_hasta)}</div>
          )}
          {e.titulo && <h3 className={`font-condensed font-black text-azul leading-tight truncate ${grande ? 'text-2xl' : 'text-xl'}`}>{e.titulo}</h3>}
          {e.descripcion && <p className="text-gray-500 text-sm mt-1 line-clamp-2">{e.descripcion}</p>}
          {!e.titulo && !e.descripcion && !e.visible_hasta && (
            <p className="text-gray-400 text-sm">{e.tipo === 'imagen' ? 'Ver imagen' : 'Ver video'}</p>
          )}
        </div>
      </button>
    );
  };

  // ---------- ventana (modal) ----------
  const actualEl = abierta !== null ? ordenados[abierta] : null;

  const contenidoModal = (e: Elemento) => {
    if (e.tipo === 'imagen' && e.archivo_url) {
      return <img src={e.archivo_url} alt={e.titulo || 'TicoBaterías'} className="max-h-[78vh] w-auto max-w-full mx-auto rounded-xl shadow-2xl" />;
    }
    if (e.tipo === 'video' && e.archivo_url) {
      return <video key={e.id} src={e.archivo_url} controls autoPlay playsInline className="block w-full max-w-4xl max-h-[78vh] mx-auto rounded-xl shadow-2xl bg-black" />;
    }
    const inc = e.enlace_url ? incrustar(e.enlace_url) : null;
    if (inc) {
      return (
        <div className="mx-auto bg-black rounded-xl overflow-hidden shadow-2xl"
          style={inc.vertical
            ? { aspectRatio: '9 / 16', height: '78vh', maxWidth: '100%' }
            : { aspectRatio: '16 / 9', width: 'min(100%, calc(78vh * 16 / 9))' }}>
          <iframe key={e.id} src={inc.src} title={e.titulo || `Video de ${inc.red}`} className="w-full h-full block border-0"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowFullScreen />
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-sm bg-azul rounded-2xl p-8 text-center shadow-2xl">
        <div className="text-5xl mb-3">▶️</div>
        <a href={e.enlace_url || '#'} target="_blank" rel="noreferrer noopener"
          className="inline-block bg-rojo hover:bg-rojo-dark text-white font-bold px-6 py-3 rounded-xl">
          Ver en {dominio(e.enlace_url || '')}
        </a>
      </div>
    );
  };

  const rejilla = 'grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="bg-azul py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-white/40 text-xs tracking-widest mb-2">MERCADEO</div>
          <h1 className="font-condensed text-5xl font-black text-white">Promociones y novedades</h1>
          <p className="text-white/60 mt-2">Ofertas, instalaciones y lo último de TicoBaterías en fotos y videos</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {pestanas.length > 2 && (
          <div className="flex gap-2 flex-wrap mb-8">
            {pestanas.map(p => (
              <button key={p.valor || 'todo'} type="button" onClick={() => elegir(p.valor)}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${seleccion === p.valor ? 'bg-azul text-white border-azul' : 'bg-white text-gray-600 border-gray-200 hover:border-azul hover:text-azul'}`}>
                {p.texto} <span className={seleccion === p.valor ? 'text-white/60' : 'text-gray-400'}>({p.cantidad})</span>
              </button>
            ))}
          </div>
        )}

        {cargando ? (
          <div className={rejilla}>
            {[1, 2, 3, 4].map(i => <div key={i} className="aspect-[4/3] bg-gray-200 rounded-2xl animate-pulse" />)}
          </div>
        ) : total > 0 ? (
          <>
            {destacados.length > 0 && (
              <div className={`grid gap-5 mb-8 ${destacados.length === 1 ? 'md:grid-cols-1 max-w-4xl' : 'md:grid-cols-2'}`}>
                {destacados.map(e => tarjeta(e, true))}
              </div>
            )}
            {resto.length > 0 && (
              <div className={rejilla}>
                {resto.map(e => tarjeta(e, false))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-4">📣</div>
            <h3 className="font-condensed text-xl font-black text-azul mb-2">Próximamente</h3>
            <p className="text-gray-400 text-sm">Muy pronto vas a encontrar aquí nuestras promociones y novedades.</p>
          </div>
        )}

        <section className="mt-12 bg-azul rounded-2xl px-6 py-8 text-center border-b-4 border-rojo">
          <h2 className="font-condensed text-3xl font-black text-white">Síguenos en redes</h2>
          <p className="text-white/60 text-sm mt-1 mb-5">Ofertas y consejos para tu batería todas las semanas</p>
          <RedesSociales tamano={44} conTexto className="flex flex-wrap items-center justify-center gap-3" />
        </section>
      </div>

      {actualEl && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4 md:px-20" onClick={() => setAbierta(null)} role="dialog" aria-modal="true">
          <button type="button" onClick={() => setAbierta(null)} className="absolute top-4 right-5 text-white text-3xl hover:text-gray-300 z-10" aria-label="Cerrar">✕</button>
          {total > 1 && (
            <>
              <button type="button" aria-label="Anterior"
                onClick={ev => { ev.stopPropagation(); setAbierta(((abierta as number) - 1 + total) % total); }}
                className="absolute left-2 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white text-2xl z-10">‹</button>
              <button type="button" aria-label="Siguiente"
                onClick={ev => { ev.stopPropagation(); setAbierta(((abierta as number) + 1) % total); }}
                className="absolute right-2 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white text-2xl z-10">›</button>
            </>
          )}
          <figure className="w-full max-w-5xl" onClick={ev => ev.stopPropagation()}>
            {contenidoModal(actualEl)}
            {(actualEl.titulo || actualEl.descripcion) && (
              <figcaption className="text-center mt-3 max-w-2xl mx-auto">
                {actualEl.titulo && <div className="text-white font-bold text-lg">{actualEl.titulo}</div>}
                {actualEl.descripcion && <div className="text-white/70 text-sm mt-1 whitespace-pre-line">{actualEl.descripcion}</div>}
              </figcaption>
            )}
            {total > 1 && <div className="text-center text-white/40 text-xs mt-2">{(abierta as number) + 1} / {total}</div>}
          </figure>
        </div>
      )}
    </main>
  );
}
